import { useObserver } from 'mobx-react-lite';
import _ from 'lodash';
import { useQuery as useApolloQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getCategoriesForMenuForState } from 'shared/helpers/products';
import { GetCollectionsNavigationDocument } from 'src/gql/graphql';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'hooks/use-cart';
import { menuFilters } from 'hooks/use-menu-filter-options';
import { useQuery } from 'hooks/use-query';
import { useUserSegments } from 'hooks/use-user-segments';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';

/**
 * @typedef {Object} Category
 * @property {string} key
 * @property {string} label
 * @property {string} value
 * @property {string} [slug]
 * @property {boolean} [customCategory]
 * @property {string} [cardImage]
 * @property {boolean} [isOutlet]
 * @property {string} [cardDisplayName]
 * @property {string} [navigationTitle]
 */

/**
 * @typedef {Object} UseDispensaryCategoriesReturn
 * @property {Category[]} categories
 */

/**
 * Hook to fetch categories for the current dispensary.
 *
 * Optimized for FCP/CLS: Initially uses collectionsNavigation from the dispensary object
 * (available immediately from SSR/cache) to render content without layout shift.
 * Then fetches user-specific collections via useGetCollectionsNavigationQuery which may include
 * personalized content based on user segments. Once user-specific collections load,
 * they replace the initial dispensary collections.
 *
 * @returns {UseDispensaryCategoriesReturn}
 */
export default function useDispensaryCategories() {
  const { dispensary } = useDispensary();
  const cart = useCart();
  const menuType = useObserver(() => cart.menuType);
  const { isKiosk } = useUI();
  const flags = useFlags();

  let categories = getCategoriesForMenuForState(dispensary?.location?.state);
  let loadedCategories = dispensary?.activeCategories || [];
  const categoriesV2 = dispensary?.webCustomizationSettingsV2?.categories?.items;
  const isEnabledCustomCategories = flags['growth.ecomm.custom-categories.rollout'] ?? false;
  const isOutletCollectionsEnabled = flags['ecomm.display-outlet-collections-consumer.rollout'] ?? false;

  /**
   * SEGMENTS IN VARIABLES FOR CACHE DIFFERENTIATION:
   *
   * User segments are fetched and included in query variables, enabling Apollo and CDN
   * to cache separately for each segment combination. This allows cache-first fetchPolicy
   * and PUBLIC CDN caching while maintaining correct per-user results.
   *
   * Different segment values in variables create different cache keys, so users with
   * different segments automatically get different cached responses.
   *
   * Server validates client-provided segments to prevent spoofing.
   */
  const { segments: userSegments } = useUserSegments({
    dispensaryId: dispensary?.id,
  });

  const { data } = useQuery(menuFilters, {
    skip: !dispensary,
    fetchPolicy: 'cache-first',
    variables: {
      dispensaryId: dispensary?.id,
      pricingType: menuType,
      isKioskMenu: isKiosk,
      userSegmentIds: userSegments.length > 0 ? userSegments : undefined,
    },
  });

  // Get collections from the initial dispensary query (available immediately from SSR/cache)
  const dispensarySuppliedCollections = dispensary?.collectionsNavigation;

  // Fetch user-specific collections via separate query for personalized content
  const { data: collectionsData } = useApolloQuery(GetCollectionsNavigationDocument, {
    skip: !isEnabledCustomCategories || !dispensary?.id,
    variables: {
      dispensaryId: dispensary?.id,
      customCategory: true,
      enabled: true,
      activeOnly: true,
    },
  });

  // Use dispensary-supplied collections initially, replace with query data when available
  // Only replace if query returns non-empty data to prevent CLS when query returns empty array
  let resolvedCollections = dispensarySuppliedCollections;
  if (collectionsData?.getCollectionsNavigation?.length > 0) {
    resolvedCollections = collectionsData.getCollectionsNavigation;
  }
  const activeCollections = useActiveScheduledItems({
    items: resolvedCollections ?? [],
    getSchedule: (collection) => collection.schedule,
    dispensaryTimezone: dispensary?.timezone,
    enablePolling: true,
  });

  const productCategories = _.get(data, `menuFiltersV2.categoriesSubcategories`, []);

  if (productCategories.length) {
    loadedCategories = productCategories.map(({ category }) => category);
  }

  if (loadedCategories.length) {
    categories = _.filter(categories, (category) => _.includes(loadedCategories, category.value));
  }

  if (categoriesV2) {
    const orderMap = new Map(categoriesV2.map((item, index) => [item.category, index]));

    if (activeCollections?.length) {
      const collectionsToShow = isOutletCollectionsEnabled
        ? activeCollections
        : activeCollections.filter((collection) => !collection.isOutlet);

      categories = [
        ...categories,
        ...collectionsToShow.map((collection) => {
          const category = {
            customCategory: collection.customCategory ?? true,
            slug: collection.slug,
            key: collection.id,
            label: collection.title,
            value: collection.title,
          };

          if (isOutletCollectionsEnabled && collection.isOutlet) {
            category.cardImage = collection.cardImage;
            category.label = collection.cardDisplayName || collection.title;
            category.isOutlet = true;
          }

          return category;
        }),
      ];
    }

    // Use Infinity as fallback so items not in orderMap appear at the end
    categories.sort((a, b) => (orderMap.get(a.value) ?? Infinity) - (orderMap.get(b.value) ?? Infinity));
  }

  return { categories };
}
